import type { UseQueryResult } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';
import {
  useGetFileMetadata,
  useGetFileMetadata2,
} from '@/generated/hooks/bishop/files/v1/api';
import type {
  V1FileMetadata,
  RpcStatus,
} from '@/generated/hooks/bishop/models';
import type { IParsedFileResource } from '@/shared/utils/file-utils';

/**
 * Fetches file metadata using the correct API endpoint based on whether the
 * resource name is workspace-scoped.
 *
 * - Names like `organizations/{org}/files/{file}` use the v1 endpoint.
 * - Names like `organizations/{org}/workspaces/{ws}/files/{file}` use the v2 endpoint.
 *
 * Both generated hooks are always called (hooks cannot be conditional) with
 * mutually exclusive `enabled` flags so only one fires a network request.
 *
 * @param parsedFile - Output of `parseFileResourceName`, or null if parsing failed
 * @returns React Query result with `V1FileMetadata` response
 */
export function useFileMetadata(
  parsedFile: IParsedFileResource | null,
): UseQueryResult<AxiosResponse<V1FileMetadata>, AxiosError<RpcStatus>> {
  const organization = parsedFile?.organization ?? '';
  const workspace = parsedFile?.workspace ?? '';
  const file = parsedFile?.file ?? '';
  const hasWorkspace = !!parsedFile?.workspace;
  const isEnabled = !!parsedFile;

  const v1Result = useGetFileMetadata(organization, file, {
    query: { enabled: isEnabled && !hasWorkspace },
  });

  const v2Result = useGetFileMetadata2(organization, workspace, file, {
    query: { enabled: isEnabled && hasWorkspace },
  });

  return hasWorkspace ? v2Result : v1Result;
}
